import { useSearch, type UseSearchProps } from "@/components/Search";
import styled from "@emotion/styled";
import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import LazyList from "@imminently/decisively-ui/LazyList";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import ListNavigation from "@imminently/decisively-ui/ListNavigation";
import Popover, { type PopoverProps } from "@imminently/decisively-ui/Popover";
import Select from "@imminently/decisively-ui/Select";
import type { BackendResource, QueryParams, Resource, ResourceService } from "@imminently/immi-query";
import { Divider } from "@mui/material";
import { useMemo } from "react";
import { type PropsWithChildren, useRef, useState } from "react";

import { DialogActions, type DialogActionsProps } from "@/ui/dialogs/actions";
import "../styles.scss";
import TextInput from "@imminently/decisively-ui/TextInput";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/ui/primitives/skeleton";

const MenuActions = DialogActions;

const PopperContent = styled(FloatingPanel)`
  min-width: 320px;
  max-width: 320px;
  padding: 0.5rem;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.75rem;
  padding: 0;
  text-transform: uppercase;
`;

export type ComboboxProps<T extends Resource> = {
  label: string;
  service: ResourceService<T>;
  filter?: QueryParams;
  renderItem: (item: BackendResource<T>) => React.ReactNode;
  value?: string | null;
  onSelect: (item: BackendResource<T>) => void;
  searchProps?: UseSearchProps;
  actions?: DialogActionsProps;
  side?: "left" | "right";
  open?: boolean;
  setOpen?: (open: boolean) => void;
} & PropsWithChildren;

export const ImmiCombobox = <T extends Resource>({
                                                   label,
                                                   service,
                                                   filter,
                                                   searchProps,
                                                   renderItem,
                                                   value,
                                                   onSelect,
                                                   actions,
                                                   side,
                                                   open: openProp,
                                                   setOpen: setOpenProp,
                                                   children,
                                                 }: ComboboxProps<T>) => {
  const PAGE_SIZE = 10;
  const { t } = useTranslation();
  const { filter: sFilter, ...search } = useSearch(searchProps);
  const [ openState, setOpenState ] = useState(false);
  const open = openProp ?? openState;
  const setOpen = setOpenProp ?? setOpenState;
  const shouldQuery = open;
  const listHandleRef = useRef<{ scrollToIndex: (index: number) => void } | null>(null);

  const { data, total, query, loadMore } = service.useInfiniteResources({
    enabled: shouldQuery,
    perPage: PAGE_SIZE,
    filter: {
      name: sFilter.length > 0 ? sFilter : undefined,
      // sort: { field: 'created', order: 'desc' },
      ...filter,
    },
  });

  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);
  const placement: PopoverProps["placement"] = side ? `${side}-start` : "bottom-start";

  const heading = <span className="flex items-center pt-2 gap-1">{label} • {query.isLoading ?
    <LoadingSpinner className="inline-block" size="sm"/> : total}</span>;
  const nextPageSkeletonCount = Math.max(1, Math.min(PAGE_SIZE, Math.max(0, total - data.length)));

  return (
    <Popover
      visible={open}
      onVisibleChange={(v) => setOpen(v)}
      mode="click"
      interactable={true}
      placement={placement}
      autoFocus={true}
    >
      <Popover.Reference asChild>{children}</Popover.Reference>
      <Popover.Content asChild>
        <PopperContent>
          <ListNavigation
            onItemActivated={(id) => {
              if (!id) return;
              const itemId = id.replace("combo-item-", "");
              const index = data.findIndex((item) => item.id === itemId);
              if (index >= 0) {
                listHandleRef.current?.scrollToIndex(index);
              }
            }}
          >
            <ListNavigation.Input asChild>
              <TextInput
                autoFocus
                placeholder={search.placeholder}
                value={search.value}
                onValueChange={search.setValue}
                fullWidth
              />
            </ListNavigation.Input>
            <ListHeader>{heading}</ListHeader>
            {!query.isLoading && data?.length === 0 ? (
                <div className="text-center p-4">{t("no_results")}</div>
              ) :
              <ListNavigation.List asChild>
                <LazyList
                  handleRef={listHandleRef}
                  className="max-h-55 -mx-2"
                  scrollClassName="scrollable"
                  items={data}
                  getItemKey={(item) => item.id}
                  hasMore={canLoad}
                  loadingMore={Boolean(query.isFetchingNextPage)}
                  onReachEnd={() => loadMore()}
                  skeletonRows={query.isLoading ? PAGE_SIZE : query.isFetchingNextPage ? nextPageSkeletonCount : 0}
                  renderSkeletonItem={(index) => (
                    <div key={`combo-skeleton-${index}`} className="w-full px-2 py-1">
                      <Skeleton className="h-4 w-full rounded-sm"/>
                    </div>
                  )}
                  renderItem={(item) => (
                    <ListNavigation.Item
                      key={item.id}
                      id={`combo-item-${item.id}`}
                      onSelect={() => {
                        setOpen(false);
                        search.setValue("");
                        onSelect(item);
                      }}
                      asChild
                    >
                      <Select.MenuItem
                        isSelected={Boolean(value && item.id === value)}
                      >
                        {renderItem(item)}
                      </Select.MenuItem>
                    </ListNavigation.Item>
                  )}
                />
              </ListNavigation.List>}
          </ListNavigation>
          {actions ? (
            <>
              <Divider style={{ margin: "0 -0.5rem 0.5rem -0.5rem" }}/>
              {/* @ts-ignore */}
              <MenuActions
                {...actions}
                className="p-0 gap-0 justify-between"
              />
            </>
          ) : null}
        </PopperContent>
      </Popover.Content>
    </Popover>
  );
};
